import React, { useEffect, useMemo, useState } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { fournisseurs as api } from '../services/api';
import { nb, eur, eurCourt, pct1, moisAn, usePanneau, basculer } from './ventesUtils';
import { CATS, DIMS, dimObj, deltaPct, Delta, signePct1, useFiltresFourn, PanneauFourn, Avertissements, Sparkline, ModaleEcritures } from './fournisseursUtils';
import './Ventes.css';
import './Fournisseurs.css';

// ─── Fournisseurs & charges — Performance ────────────────────────────────────
// Lecture d'ensemble (KPI vs N-1, évolution mensuelle N / N-1, sociétés) puis
// LE tableau : toutes les lignes de la dimension choisie (fournisseur,
// regroupement, service, type de coût, compte, société), triables sur chaque
// colonne, filtrables au clavier, dépliables (regroupement → fournisseurs,
// fournisseur → comptes) et ouvrant les écritures.

const COLS = [
  { cle: 'nom', label: 'Nom', l: true },
  { cle: 'nbFourn', label: 'Fourn.', title: 'Nombre de fournisseurs distincts', groupe: true },
  { cle: 'nb', label: 'Écritures' },
  { cle: 'montant', label: 'Montant HT' },
  { cle: 'part', label: 'Part' },
  { cle: 'n1', label: 'N-1' },
  { cle: 'delta', label: 'Δ N-1' },
  { cle: 'deltaPct', label: 'Δ %' },
  { cle: 'serie', label: 'Tendance', title: 'Montant mensuel sur la période', tri: false },
];
const TAILLES = [25, 100, 0];
const norm = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

function Kpi({ l, v, n1, fmt = eurCourt, sub, mode = 'eur' }) {
  const d = n1 ? v - n1 : null;
  return (
    <div className="vt-kpi">
      <div className="l">{l}</div>
      <div className="v">{fmt(v)}</div>
      {d == null ? <div className="d"><small>{sub || 'pas de N-1'}</small></div>
        : <div className={`d ${d > 0 ? 'fr-up' : d < 0 ? 'fr-down' : ''}`}>{mode === 'eur' ? (d > 0 ? '+' : '') + eurCourt(d) : (d > 0 ? '+' : '') + nb(d)} <small>· {signePct1(deltaPct(v, n1))} vs N-1{sub ? ` · ${sub}` : ''}</small></div>}
    </div>
  );
}

export default function Fournisseurs() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(null);
  const [tabLoading, setTabLoading] = useState(false);
  const [erreur, setErreur] = useState('');
  const [dim, setDim] = useState('fournisseur');
  const [tri, setTri] = useState({ cle: 'montant', sens: -1 });
  const [recherche, setRecherche] = useState('');
  const [taille, setTaille] = useState(25);
  const [ouverts, setOuverts] = useState({}); // { [nom]: rows | 'loading' }
  const [modale, setModale] = useState(null);
  const [ouvert, toggle] = usePanneau();
  const societesDispo = useMemo(() => data?.societesDispo || [], [data]);
  const f = useFiltresFourn(societesDispo);

  useEffect(() => {
    let actif = true; setErreur('');
    api.charger(f.query).then((r) => { if (actif) setData(r.data); }).catch((e) => { if (actif) setErreur(e.response?.data?.error || 'Erreur de chargement'); });
    return () => { actif = false; };
  }, [f.cle]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let actif = true; setTabLoading(true); setOuverts({});
    api.tableau({ ...f.query, dim }).then((r) => { if (actif) { setTab(r.data); setTabLoading(false); } }).catch(() => { if (actif) setTabLoading(false); });
    return () => { actif = false; };
  }, [f.cle, dim]); // eslint-disable-line react-hooks/exhaustive-deps

  const changerDim = (d) => { setDim(d); setRecherche(''); setTri({ cle: 'montant', sens: -1 }); };
  const trier = (cle) => setTri((t) => (t.cle === cle ? { cle, sens: -t.sens } : { cle, sens: cle === 'nom' ? 1 : -1 }));

  const total = tab ? tab.rows.reduce((t, r) => t + r.montant, 0) : 0;
  const lignes = useMemo(() => {
    if (!tab) return [];
    const q = norm(recherche.trim());
    let l = tab.rows.map((r) => ({ ...r, part: total ? r.montant / total * 100 : 0, delta: r.n1 ? r.montant - r.n1 : null, deltaPct: deltaPct(r.montant, r.n1) }));
    if (q) l = l.filter((r) => norm(r.nom).includes(q) || norm(r.sub).includes(q));
    const { cle, sens } = tri;
    l.sort((a, b) => {
      const va = a[cle], vb = b[cle];
      if (cle === 'nom') return sens * a.nom.localeCompare(b.nom, 'fr');
      if (va == null && vb == null) return 0; if (va == null) return 1; if (vb == null) return -1;
      return sens * (va - vb);
    });
    return l;
  }, [tab, recherche, tri, total]);
  const visibles = taille ? lignes.slice(0, taille) : lignes;
  const totVis = { montant: lignes.reduce((t, r) => t + r.montant, 0), n1: lignes.reduce((t, r) => t + r.n1, 0), nb: lignes.reduce((t, r) => t + r.nb, 0) };

  const deplier = async (nom) => {
    if (ouverts[nom]) { setOuverts((o) => { const c = { ...o }; delete c[nom]; return c; }); return; }
    setOuverts((o) => ({ ...o, [nom]: 'loading' }));
    try { const r = await api.tableau({ ...f.query, dim, drill: nom }); setOuverts((o) => ({ ...o, [nom]: r.data.rows || [] })); }
    catch (e) { setOuverts((o) => ({ ...o, [nom]: [] })); }
  };
  const ouvrirEcritures = async (nom) => {
    setModale({ nom, lignes: null });
    try { const r = await api.detail({ ...f.query, nom }); setModale(r.data); }
    catch (e) { setModale({ nom, lignes: [], erreur: e.response?.data?.error || 'Erreur' }); }
  };

  const Panneau = <PanneauFourn ouvert={ouvert} onToggle={toggle} f={f} societesDispo={societesDispo} />;
  if (erreur) return <><main className="vt-page"><div className="bi-empty"><h2>Rapport indisponible</h2><p>{erreur}</p></div></main>{Panneau}</>;
  if (!data) return <><main className="vt-page"><div className="vt-skel" /></main>{Panneau}</>;

  const { kpi, split, splitN1, mensuel, parSociete } = data;
  const vide = kpi.total === 0 && kpi.totalN1 === 0;
  const D = dimObj(dim);
  const sousD = dimObj(D.sous);
  const catFiltre = f.catsActives.length < CATS.length;
  const totalSplit = split.cogs + split.fg + split.autres;
  const catLabel = catFiltre ? f.catsActives.map((c) => CATS.find((x) => x.code === c)?.nom).join(' + ') : null;
  const graphe = mensuel.map((m) => ({ mois: m.mois, N: m.montant, 'N-1': m.n1 }));

  return (
    <>
      <main className="vt-page">
        <div className="vt-head">
          <div>
            <h1>Fournisseurs — Performance</h1>
          </div>
          <div className="vt-asof"><b>{data.periode}</b><br />vs mêmes mois N-1 · HT{catLabel ? <><br />{catLabel}</> : ''}</div>
        </div>
        <Avertissements data={data} />

        {vide ? (
          <div className="bi-empty" style={{ marginTop: 20 }}><h2>Aucune donnée</h2><p>Ajustez les filtres (société, catégorie, période).</p></div>
        ) : (
          <>
            <div className="vt-kpis">
              <Kpi l={catFiltre ? 'Charges (filtre catégorie)' : 'Total des charges'} v={kpi.total} n1={kpi.totalN1} />
              <Kpi l="Achats marchandises" v={split.cogs} n1={splitN1.cogs} sub="véhicules & pièces" />
              <Kpi l="Frais généraux" v={split.fg} n1={splitN1.fg} sub="charges d'exploitation" />
              <Kpi l="Fournisseurs actifs" v={kpi.nbFourn} n1={kpi.nbFournN1} fmt={nb} mode="nb" sub={`${kpi.nouveaux} sans N-1`} />
              <div className="vt-kpi"><div className="l">Concentration</div><div className="v">{pct1(kpi.total ? kpi.top10 / kpi.total * 100 : 0)}</div><div className="d"><small>des charges chez les 10 premiers fournisseurs</small></div></div>
            </div>
            {!catFiltre && (
              <div className="fr-cat" style={{ marginTop: 12 }}>
                {CATS.map((c) => split[c.code] > 0 && <div key={c.code} style={{ flexGrow: split[c.code], background: c.couleur, color: c.code === 'autres' ? '#475569' : '#fff' }} title={`${c.nom} · ${eur(split[c.code])}`}>{c.nom} · {eurCourt(split[c.code])} ({pct1(split[c.code] / totalSplit * 100)})</div>)}
              </div>
            )}

            <div className="vt-two" style={{ marginTop: 18 }}>
              <div className="vt-card">
                <div className="vt-card-h"><h2>Évolution mensuelle — N vs N-1</h2><span className="hint">{catLabel || 'toutes catégories'}</span></div>
                <div className="vt-card-b">
                  <ResponsiveContainer width="100%" height={230}>
                    <BarChart data={graphe} margin={{ top: 6, right: 8, bottom: 0, left: 6 }} barGap={2}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default,#e2e8f0)" vertical={false} />
                      <XAxis dataKey="mois" tickFormatter={moisAn} fontSize={10.5} minTickGap={16} />
                      <YAxis tickFormatter={(v) => eurCourt(v)} fontSize={10.5} width={52} />
                      <Tooltip formatter={(v, n) => [eur(v), n === 'N' ? 'Période' : 'Mêmes mois N-1']} labelFormatter={moisAn} />
                      <Legend iconType="square" iconSize={9} wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="N-1" fill="#c9d0d9" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="N" fill="#0F766E" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="vt-card">
                <div className="vt-card-h"><h2>Par société</h2></div>
                <div className="vt-card-b vt-tbl-wrap">
                  <table className="vt-tbl">
                    <thead><tr><th className="l">Société</th><th>Fourn.</th><th>Montant HT</th><th>N-1</th><th className="vt-dh">Δ</th></tr></thead>
                    <tbody>
                      {parSociete.map((s) => (
                        <tr key={s.code} className={`tbl-clic${f.societesUrl === s.code ? ' actif' : ''}`}
                            onClick={() => f.setFiltre({ societes: basculer(f.societesUrl, s.code) })}>
                          <td className="l"><i className="fr-dot" style={{ background: f.couleurDe[s.code] }} />{s.nom}</td>
                          <td>{nb(s.nbFourn)}</td><td><b>{eurCourt(s.montant)}</b></td><td>{eurCourt(s.n1)}</td><Delta valeur={s.montant} n1={s.n1} mode="pct" />
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="vt-card">
              <div className="vt-card-h"><h2>Tableau complet</h2></div>
              <div className="fr-toolbar">
                <span className="vt-seg">{DIMS.map((d) => <button key={d.code} className={dim === d.code ? 'on' : ''} onClick={() => changerDim(d.code)}>{d.label}</button>)}</span>
                <input className="fr-search" type="search" placeholder={`Rechercher un ${D.un}…`} value={recherche} onChange={(e) => setRecherche(e.target.value)} />
                <span className="fr-count">{lignes.length === (tab?.rows.length || 0) ? `${nb(lignes.length)} ${D.un}${lignes.length > 1 ? 's' : ''}` : `${nb(lignes.length)} / ${nb(tab?.rows.length || 0)}`}{tabLoading ? ' · chargement…' : ''}</span>
                {lignes.length > 25 && <span className="fr-chips">{TAILLES.map((t) => <button key={t} className={taille === t ? 'on' : ''} onClick={() => setTaille(t)}>{t || 'Tous'}</button>)}</span>}
              </div>
              <div className="vt-card-b vt-tbl-wrap" style={{ paddingTop: 0 }}>
                <table className={`vt-tbl fr-tbl-big${tabLoading ? ' fr-fade' : ''}`}>
                  <thead><tr>
                    <th className="fr-rank">#</th>
                    {COLS.filter((c) => !c.groupe || dim !== 'fournisseur').map((c) => (
                      <th key={c.cle} className={`${c.l ? 'l' : ''} ${c.tri === false ? '' : 'fr-sort'} ${tri.cle === c.cle ? 'on' : ''}`} title={c.title} onClick={c.tri === false ? undefined : () => trier(c.cle)} aria-sort={tri.cle === c.cle ? (tri.sens > 0 ? 'ascending' : 'descending') : 'none'}>
                        {c.label}{c.tri !== false && <span className="fr-arrow">{tri.cle === c.cle ? (tri.sens > 0 ? '▲' : '▼') : '⇅'}</span>}
                      </th>
                    ))}
                    <th />
                  </tr></thead>
                  <tbody>
                    {visibles.map((r, i) => {
                      const sub = ouverts[r.nom];
                      const estFourn = dim === 'fournisseur';
                      return (
                        <React.Fragment key={r.nom}>
                          <tr className={sub ? 'fr-open' : ''}>
                            <td className="fr-rank">{i + 1}</td>
                            <td className="l fr-nom" onClick={() => deplier(r.nom)} title={`${r.nom} — détail par ${sousD.un}`}>
                              <span className="chev">▶</span><span className="fr-nom-t">{r.nom}</span>{r.sub && <span className="fr-sub">{r.sub}</span>}
                            </td>
                            {!estFourn && <td>{nb(r.nbFourn)}</td>}
                            <td>{nb(r.nb)}</td>
                            <td><b>{eurCourt(r.montant)}</b></td>
                            <td>{pct1(r.part)}</td>
                            <td>{r.n1 ? eurCourt(r.n1) : '—'}</td>
                            <Delta valeur={r.montant} n1={r.n1} />
                            <Delta valeur={r.montant} n1={r.n1} mode="pct" />
                            <td className="fr-sparkc"><Sparkline serie={r.serie} largeur={60} /></td>
                            <td className="fr-act">{estFourn && <button className="fr-btn fr-btn-ico" title="Voir les écritures du grand livre" aria-label="Écritures" onClick={() => ouvrirEcritures(r.nom)}>≡</button>}</td>
                          </tr>
                          {sub === 'loading' && <tr className="fr-sub-row"><td /><td className="l" colSpan={9}>Chargement…</td></tr>}
                          {Array.isArray(sub) && sub.length === 0 && <tr className="fr-sub-row"><td /><td className="l" colSpan={9}>Aucun détail.</td></tr>}
                          {Array.isArray(sub) && sub.map((s) => (
                            <tr className="fr-sub-row" key={s.nom}>
                              <td />
                              <td className="l" title={s.nom}><span className="fr-nom-t">{s.nom}</span>{s.sub && s.sub !== s.nom && <span className="fr-sub">{s.sub}</span>}</td>
                              {!estFourn && <td>{nb(s.nbFourn)}</td>}
                              <td>{nb(s.nb)}</td>
                              <td>{eurCourt(s.montant)}</td>
                              <td className="fr-part">{pct1(r.montant ? s.montant / r.montant * 100 : 0)}</td>
                              <td>{s.n1 ? eurCourt(s.n1) : '—'}</td>
                              <Delta valeur={s.montant} n1={s.n1} />
                              <Delta valeur={s.montant} n1={s.n1} mode="pct" />
                              <td className="fr-sparkc"><Sparkline serie={s.serie} largeur={60} /></td>
                              <td className="fr-act">{!estFourn && <button className="fr-btn fr-btn-ico" title="Voir les écritures du grand livre" aria-label="Écritures" onClick={() => ouvrirEcritures(s.nom)}>≡</button>}</td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                    {visibles.length === 0 && <tr><td colSpan={11} className="fr-empty-tbl">{tabLoading ? 'Chargement…' : 'Aucune ligne.'}</td></tr>}
                  </tbody>
                  <tfoot><tr>
                    <td /><td className="l">Total {recherche ? 'des lignes trouvées' : ''}</td>
                    {dim !== 'fournisseur' && <td />}
                    <td>{nb(totVis.nb)}</td><td>{eurCourt(totVis.montant)}</td><td>{pct1(total ? totVis.montant / total * 100 : 0)}</td><td>{eurCourt(totVis.n1)}</td>
                    <Delta valeur={totVis.montant} n1={totVis.n1} /><Delta valeur={totVis.montant} n1={totVis.n1} mode="pct" /><td /><td />
                  </tr></tfoot>
                </table>
                {taille && lignes.length > taille ? <p className="vt-note">{nb(lignes.length - taille)} lignes masquées — <button className="fr-link" onClick={() => setTaille(0)}>tout afficher</button>.</p> : null}
              </div>
            </div>
          </>
        )}
      </main>
      <ModaleEcritures drill={modale} onFermer={() => setModale(null)} />
      {Panneau}
    </>
  );
}
